import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import { ActionBar } from '~/components/ui/action-bar';
import { InlineMarkdown } from '~/components/inline-markdown';
import { cn } from '~/lib/utils';
import { PickerDialogShell } from './picker-dialog-shell';
import {
  agentAccessKeys,
  filterOfferableAgents,
  normalizeAgentSearch,
  MAX_APP_AGENTS,
  type AgentAccessResponse,
  type AgentAccessSaveResponse,
} from './agent-access-keys';

/**
 * The wire contract's save-error codes, translated into something an app
 * owner can act on. `ApiError`'s message is the raw `{error}` string off a
 * non-2xx JSON response (`lib/api-client.ts`'s `describeErrorResponse`) — a
 * code like `too_many_agents` is meant for a client to branch on, not for a
 * person to read, so map the ones the wire contract names and fall back to
 * the raw string for anything unrecognized (e.g. a genuine 500 body).
 */
const AGENT_ACCESS_SAVE_ERROR_MESSAGES: Record<string, string> = {
  too_many_agents: `You can allowlist at most ${MAX_APP_AGENTS} agents for one app.`,
  not_offerable: 'One or more of those agents can no longer be offered to apps — refresh and try again.',
  catalogue_unavailable: "Couldn't reach your agent catalogue just now. Try again shortly.",
  agent_access_save_failed: "Couldn't save. Try again.",
};

function describeAgentAccessSaveError(e: Error): string {
  return AGENT_ACCESS_SAVE_ERROR_MESSAGES[e.message] ?? e.message;
}

/**
 * Drop any stale id from a selection before it rides in a Save PUT. Same D6
 * shape as `tool-picker-model.ts`'s `buildSavePayload`: `selected` (and a
 * draft toggled from it) legitimately carries a stale id — the route only
 * REPORTS staleness, it never auto-prunes — but the route rejects the whole
 * payload if it contains a `not_offerable` id, so sending a stale-carrying
 * selection back byte-for-byte would fail every future save the moment one
 * entry goes stale. Exported so the guarantee is tested directly against a
 * hand-built input.
 */
export function buildAgentSavePayload(current: string[], staleIds: Set<string>): string[] {
  return current.filter((id) => !staleIds.has(id));
}

/** Order-insensitive set equality — same shape as `tool-picker-dialog.tsx`'s sibling helper. */
function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((x) => set.has(x));
}

/**
 * Whether an individual, non-stale row's checkbox should be disabled, and
 * why — derived as ONE decision, never two (FRONT-1670's rule): `disabled`
 * is simply `reason !== null`, computed by the SAME call, so a UI that reads
 * `disabled` and a UI that reads the reason can never disagree the way a
 * hand-maintained `disabled={...}` beside a hand-maintained tooltip can.
 *
 * The only reason a row can be blocked here is the backend's hard cap
 * (`PUT`'s `too_many_agents` 400, `MAX_APP_AGENTS`). An already-CHECKED row
 * must stay togglable regardless of the cap — otherwise Discard would be
 * the only way back under the limit once it's reached.
 */
export function agentRowDisabledReason(opts: {
  checked: boolean;
  selectedCount: number;
}): string | null {
  if (opts.checked) return null;
  if (opts.selectedCount >= MAX_APP_AGENTS) {
    return `You can select up to ${MAX_APP_AGENTS} agents — untick one to add another.`;
  }
  return null;
}

export interface AgentPickerDialogProps {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The owner-facing picker for FRONT-1714's per-app Eddy-agent allowlist.
 *
 * Structurally the twin of `ToolPickerDialog` — same pinned header /
 * internally-scrolling body / pinned `ActionBar`, same `draft ?? selected`
 * pattern, same `buildSavePayload`-shaped stale stripping, same stale-rows-
 * render-first-checked-and-disabled treatment — with two differences the
 * tool picker doesn't need:
 *
 * 1. A DEGRADED banner takes the stale block's place whenever the catalogue
 *    fetch itself failed upstream (`catalogueDegraded`). The route answers
 *    with an EMPTY `agents` list on that hiccup, so computing "stale" the
 *    naive way (anything in `selected` with no matching catalogue row) would
 *    paint the owner's WHOLE allowlist red and invite them to delete
 *    entries that are actually fine. The banner says plainly that nothing
 *    has changed and offers no "Remove them" action.
 * 2. An agent doesn't carry a single visible risk level the way a tool
 *    does — allowlisting it hands it its OWN tools, run as the app owner.
 *    Where the catalogue carries an agent's `tools`, they're listed on the
 *    row so the owner ticks with their eyes open, and the dialog copy says
 *    as much up front, alongside who can actually trigger a run and how
 *    long one can take.
 */
export function AgentPickerDialog({ slug, open, onOpenChange }: AgentPickerDialogProps) {
  const qc = useQueryClient();
  const searchInputRef = useRef<HTMLInputElement>(null); // Focus on open — see `picker-dialog-shell.tsx`.

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Same reset-on-open rationale as `ToolPickerDialog`: this component stays
  // mounted for the whole app-editor session (only the Dialog's own visible
  // content unmounts/remounts with `open`), so its hooks don't reset for
  // free the way a genuinely unmounted component's would.
  useEffect(() => {
    if (open) {
      setSearch('');
      setDebouncedSearch('');
    }
  }, [open]);

  // Shares its cache entry with `CapabilitiesSection`'s own query — one list
  // per app (no search-scoped variant, see agent-access-keys.ts) — so
  // opening this dialog doesn't refetch when the card's own query already
  // has the data warm.
  const { data, isLoading, isError } = useQuery({
    queryKey: agentAccessKeys.list(slug),
    queryFn: () => api.get<AgentAccessResponse>(`/api/admin/apps/${slug}/agent-access`),
    staleTime: 30_000,
  });

  const agents = data?.agents ?? [];
  const selected = data?.selected ?? [];
  const catalogueDegraded = data?.catalogueDegraded ?? false;
  // Never compute a stale set while the catalogue is degraded (see the
  // `AgentAccessResponse.catalogueDegraded` doc comment) — derived
  // defensively from the flag here rather than trusted straight off
  // `data.stale`, so a future backend regression that forgot to suppress
  // `stale` during a degraded fetch still can't repaint this owner's whole
  // allowlist red.
  const stale = catalogueDegraded ? [] : (data?.stale ?? []);
  const staleIds = useMemo(() => new Set(stale), [stale]);

  const normalizedSearch = normalizeAgentSearch(debouncedSearch);
  const filteredAgents = useMemo(
    () => filterOfferableAgents(agents, debouncedSearch),
    [agents, debouncedSearch],
  );

  const save = useMutation({
    mutationFn: (agentIds: string[]) =>
      api.put<AgentAccessSaveResponse>(`/api/admin/apps/${slug}/agent-access`, { agentIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentAccessKeys.lists(slug) });
      toast.success('Agent access saved');
    },
    onError: (e: Error) => toast.error(`Save failed: ${describeAgentAccessSaveError(e)}`),
  });

  const [draft, setDraft] = useState<string[] | null>(null);
  const current = draft ?? selected;
  useEffect(() => {
    // Only clear an in-progress draft once it has actually been PERSISTED —
    // same FRONT-1385 review-finding-11 fix as `ToolPickerDialog`: compare
    // the draft's own SAVE PAYLOAD (not the raw draft, which may still
    // carry a stale id the payload strips) against the fresh `selected`, so
    // a stray refetch (window refocus, the 30s staleTime lapsing) never
    // silently discards the owner's unsaved ticks.
    if (draft !== null && sameSet(buildAgentSavePayload(draft, staleIds), selected)) {
      setDraft(null);
    }
    // `draft` is intentionally excluded — this effect reacts to fresh
    // SERVER data, never to the owner's own local edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, staleIds]);

  function toggle(id: string) {
    // Stale rows aren't individually toggleable — "Remove them" is the only
    // way to clear one (D6: never silently pruned by an ordinary uncheck).
    if (staleIds.has(id)) return;
    setDraft((prev) => {
      const base = prev ?? selected;
      const checked = base.includes(id);
      // The 10-agent cap only ever blocks ADDING — an already-checked row
      // must stay removable regardless (see `agentRowDisabledReason`).
      if (!checked && agentRowDisabledReason({ checked: false, selectedCount: base.length })) {
        return base;
      }
      return checked ? base.filter((x) => x !== id) : [...base, id];
    });
  }

  // Compares against `selected` (the server-confirmed value), never
  // `current` — same reasoning as `ToolPickerDialog`'s `dirty`.
  const dirty = draft !== null && !sameSet(draft, selected);

  function removeStale() {
    save.mutate(buildAgentSavePayload(selected, staleIds));
  }

  return (
    <PickerDialogShell
      open={open}
      onOpenChange={onOpenChange}
      eyebrow="app capabilities"
      title="Select agents"
      initialFocusRef={searchInputRef}
      description={
        <>
          Pick which of your EDDY agents this app may run via{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-foreground">eddy.agents.*</code>.
          An agent isn't one tool with a single visible risk — it uses its own tools, as you,
          and some of those change things, so pick only what the app genuinely needs. Only
          people signed in to EDDY in this workspace can run one, and a run can take minutes.
        </>
      }
      footer={
        <ActionBar
          dirty={dirty}
          saving={save.isPending}
          onSave={() => save.mutate(buildAgentSavePayload(current, staleIds))}
          onDiscard={() => setDraft(null)}
          message={
            dirty
              ? `${current.length} agent${current.length === 1 ? '' : 's'} selected`
              : undefined
          }
          className="px-[22px]"
        />
      }
    >
          {isLoading && <Skeleton className="h-48 w-full shrink-0" />}
          {!isLoading && isError && (
            <p className="text-xs text-muted-foreground">Couldn't load your agents. Try again.</p>
          )}
          {!isLoading && !isError && (
            <>
              {catalogueDegraded ? (
                <div className="shrink-0 rounded border border-border bg-muted/40 px-3 py-2">
                  <p className="text-xs text-muted-foreground">
                    Couldn't check your agents just now — nothing has changed.
                  </p>
                </div>
              ) : (
                stale.length > 0 && (
                  <div className="shrink-0 rounded border border-destructive/30 bg-destructive/5 divide-y divide-destructive/20">
                    {stale.map((id) => {
                      const rowId = `a-stale-${id}`;
                      return (
                        <div key={id} className="flex items-center gap-2 px-3 py-2">
                          <Checkbox id={rowId} checked disabled />
                          <label htmlFor={rowId} className="min-w-0 truncate text-sm font-mono">
                            {id}
                          </label>
                          <Badge variant="destructive-soft" className="ml-1 shrink-0">
                            No longer available
                          </Badge>
                        </div>
                      );
                    })}
                    <div className="flex justify-end px-3 py-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={removeStale}
                        disabled={save.isPending}
                      >
                        Remove them
                      </Button>
                    </div>
                  </div>
                )
              )}

              <Input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search agents by name…"
                aria-label="Search agents"
                className="shrink-0"
              />

              <div className="min-h-0 flex-1 overflow-y-auto rounded border border-border/60 divide-y divide-border/60">
                {filteredAgents.length === 0 && (
                  <p className="text-xs text-muted-foreground px-3 py-2">
                    {normalizedSearch ? 'No matches.' : 'No agents available yet.'}
                  </p>
                )}
                {filteredAgents.map((a) => {
                  const id = `a-${a.id}`;
                  const checked = current.includes(a.id);
                  const disabledReason = agentRowDisabledReason({
                    checked,
                    selectedCount: current.length,
                  });
                  return (
                    <label
                      key={a.id}
                      htmlFor={id}
                      className={cn(
                        'flex items-start gap-2 px-3 py-2',
                        disabledReason
                          ? 'cursor-not-allowed opacity-60'
                          : 'hover:bg-muted/40 cursor-pointer',
                      )}
                    >
                      <Checkbox
                        id={id}
                        checked={checked}
                        disabled={!!disabledReason}
                        onCheckedChange={() => toggle(a.id)}
                        className="mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground">
                          {a.name}
                        </span>
                        <InlineMarkdown
                          text={a.description}
                          as="span"
                          className="block text-xs text-muted-foreground"
                        />
                        {a.tools && a.tools.length > 0 && (
                          <span className="mt-1 flex flex-wrap gap-1">
                            {a.tools.map((t) => (
                              <Badge
                                key={t}
                                variant="outline"
                                className="font-mono text-[length:var(--text-2xs)]"
                              >
                                {t}
                              </Badge>
                            ))}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>

              {/* The cap only ever disables an unchecked row (see
                  `agentRowDisabledReason`) — this single visible line is the
                  same reason surfaced in text, not just a hover hint, per
                  the disabled-control-needs-a-visible-reason rule: a hint
                  that only exists on hover is no explanation on a touch
                  screen. */}
              {current.length >= MAX_APP_AGENTS && (
                <p className="shrink-0 text-xs text-muted-foreground">
                  You've reached the limit of {MAX_APP_AGENTS} agents for this app — untick one to
                  add another.
                </p>
              )}
            </>
          )}
    </PickerDialogShell>
  );
}
